/**
 * AxisTable (FXC-9009 two-rooms phase 5) — ONE merged axis table replacing the
 * structurally-identical SweepPanel + FillPanel twins (acceptance 13).
 *
 * Six operating axes; each row is `hold | sweep(steps) | range(min,max)`:
 *   - hold  — pinned at its live value;
 *   - sweep — part of an EXPLICIT client-built grid (linspace over the editable
 *             [lo, hi] box), the old SweepPanel contract (≤8-point batch cap);
 *   - range — part of a Tailor CAMPAIGN box, the old FillPanel contract.
 *
 * The submit CTA switches by whether ANY axis is "range" (§5.2):
 *   - no range, some sweep  → "Submit N → Tailor" grid batch (/highlift/solve);
 *   - any range             → "Fill → Tailor campaign" (budget/batch/coverage);
 *                             sweep rows join the campaign box as ranges over
 *                             their spans (a sweep IS a range the sampler may
 *                             fill adaptively — nothing is silently dropped);
 *   - everything held       → nothing to run (the spine's ▶ Solve covers the
 *                             single live point).
 *
 * Geometry axes (deploy/flaperon) need a re-mesh per value, so a cloud-only
 * host locks them to hold (same capability contract both old panels had).
 * The component is PROPS-DRIVEN for the running campaign state (the surface
 * owns create/poll/control); it holds only draft state, and it stays MOUNTED
 * across stage/filmstrip moves (draft-preservation rule, §7).
 */
import { useEffect, useMemo, useState } from "react";

import type { SolvePoint } from "../../../api/highlift";
import type {
  CampaignAxisSpec,
  CampaignControlAction,
  CampaignDetail,
} from "../../../api/highliftCampaign";
import type { HighliftPalette } from "../foil/useHighliftTheme";
import {
  FILL_AXES,
  coveragePercent,
  denseGridEquivalent,
  liveCanonicalValue,
  warmStartSavings,
  type SweepAxisId,
  type SweepLiveValues,
} from "./campaignMath";

const FONT_STACK = "'TWK Everett', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
const MONO_STACK = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";

/** Backend single-batch cap (thread-api highlift.py MAX_SOLVE_POINTS). */
export const MAX_SOLVE_POINTS = 8;
/** Campaign budget bounds — the runner's total solve-point spend. */
export const CAMPAIGN_BUDGET_MIN = 4;
export const CAMPAIGN_BUDGET_MAX = 64;
const BUDGET_DEFAULT = 24;
const BATCH_MIN = 2;
const BATCH_MAX = 8;
const BATCH_DEFAULT = 4;
const COVERAGE_CHOICES = [0.6, 0.75, 0.9] as const;
const RE_MILLION = 1e6;

export type AxisTableMode = "hold" | "sweep" | "range";

/** What the campaign CTA raises — the declarative half of CampaignCreateRequest
 *  (the surface adds solve_template/highlift_config; unchanged from FillPanel). */
export interface FillCampaignSpec {
  name: string;
  axes: CampaignAxisSpec[];
  budget: number;
  batchSize: number;
  targetCoverage: number | null;
}

export interface AxisTableProps {
  palette: HighliftPalette;
  live: SweepLiveValues;
  busy?: boolean;
  localSolveAvailable?: boolean;
  campaign?: CampaignDetail | null;
  note?: string | null;
  onSubmitGrid: (points: SolvePoint[]) => void;
  onStartCampaign: (spec: FillCampaignSpec) => void;
  onControlCampaign: (action: CampaignControlAction) => void;
}

function linspace(from: number, to: number, n: number): number[] {
  if (n <= 1) return [from];
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(from + ((to - from) * i) / (n - 1));
  out[n - 1] = to;
  return out;
}

/** Pure CTA/points derivation — exported for tests. */
export function deriveAxisPlan(
  rows: Array<{ id: SweepAxisId; mode: AxisTableMode; lo: number; hi: number; steps: number; locked: boolean }>,
  live: SweepLiveValues,
): {
  kind: "idle" | "grid" | "campaign";
  gridCount: number;
  points: SolvePoint[];
  axes: CampaignAxisSpec[];
  invalid: SweepAxisId[];
} {
  const effective = rows.map((r) => ({ ...r, mode: r.locked ? ("hold" as AxisTableMode) : r.mode }));
  const anyRange = effective.some((r) => r.mode === "range");
  const anySweep = effective.some((r) => r.mode === "sweep");
  const invalid = effective
    .filter((r) => r.mode !== "hold" && !(r.lo < r.hi))
    .map((r) => r.id);

  // Campaign axes: range rows AND sweep rows both box their spans (a sweep is
  // a range the sampler may fill adaptively); holds pin at the live value.
  const axes: CampaignAxisSpec[] = effective.map((r) => {
    const meta = FILL_AXES.find((a) => a.id === r.id)!;
    return r.mode === "hold"
      ? { axis: meta.canonical, mode: "hold" as const, value: liveCanonicalValue(r.id, live) }
      : { axis: meta.canonical, mode: "range" as const, min: r.lo, max: r.hi };
  });

  // Grid points: cartesian product of sweep linspaces × held live values.
  const valuesOf = (r: (typeof effective)[number]): number[] =>
    r.mode === "sweep" ? linspace(r.lo, r.hi, Math.max(2, r.steps)) : [liveCanonicalValue(r.id, live)];
  const byId = Object.fromEntries(effective.map((r) => [r.id, valuesOf(r)])) as Record<SweepAxisId, number[]>;
  const gridCount = (Object.values(byId) as number[][]).reduce((n, v) => n * v.length, 1);
  const points: SolvePoint[] = [];
  if (!anyRange && anySweep && gridCount <= MAX_SOLVE_POINTS && invalid.length === 0) {
    for (const deploy of byId.deploy) {
      for (const flap of byId.flaperon) {
        for (const alpha of byId.alpha) {
          for (const mach of byId.mach) {
            for (const reM of byId.re) {
              for (const blow of byId.disk_loading) {
                points.push({
                  alpha_deg: alpha,
                  mach,
                  reynolds: reM * RE_MILLION,
                  temperature: live.temperature,
                  flap_deploy_deg: deploy,
                  flaperon_deflection_deg: flap,
                  disk_thrust_loading: blow,
                });
              }
            }
          }
        }
      }
    }
  }
  return {
    kind: anyRange ? "campaign" : anySweep ? "grid" : "idle",
    gridCount,
    points,
    axes,
    invalid,
  };
}

export function AxisTable({
  palette,
  live,
  busy,
  localSolveAvailable = false,
  campaign = null,
  note,
  onSubmitGrid,
  onStartCampaign,
  onControlCampaign,
}: AxisTableProps) {
  const geometryLocked = !localSolveAvailable;
  const [modes, setModes] = useState<Record<SweepAxisId, AxisTableMode>>({
    deploy: geometryLocked ? "hold" : "sweep",
    flaperon: "hold",
    alpha: "sweep",
    mach: "hold",
    re: "hold",
    disk_loading: "hold",
  });
  const [steps, setSteps] = useState<Record<SweepAxisId, number>>({
    deploy: 4, flaperon: 3, alpha: 2, mach: 3, re: 3, disk_loading: 5,
  });
  const [boxes, setBoxes] = useState<Record<SweepAxisId, [number, number]>>(() =>
    Object.fromEntries(FILL_AXES.map((ax) => [ax.id, [...ax.range] as [number, number]])) as Record<
      SweepAxisId,
      [number, number]
    >,
  );
  const [budget, setBudget] = useState(BUDGET_DEFAULT);
  const [batchSize, setBatchSize] = useState(BATCH_DEFAULT);
  const [targetCoverage, setTargetCoverage] = useState<number | null>(null);

  // Keep the geometry axes aligned with the async host capability (both old
  // panels' rule): cloud-only forces them to hold; a local host restores the
  // default deploy sweep.
  useEffect(() => {
    setModes((m) => {
      if (!geometryLocked) return m.deploy !== "hold" ? m : { ...m, deploy: "sweep" };
      if (FILL_AXES.every((ax) => !ax.geometry || m[ax.id] === "hold")) return m;
      const next = { ...m };
      for (const ax of FILL_AXES) if (ax.geometry) next[ax.id] = "hold";
      return next;
    });
  }, [geometryLocked]);

  const rows = useMemo(
    () =>
      FILL_AXES.map((ax) => {
        const locked = geometryLocked && ax.geometry;
        const [lo, hi] = boxes[ax.id];
        return { id: ax.id, meta: ax, locked, mode: locked ? ("hold" as AxisTableMode) : modes[ax.id], lo, hi, steps: steps[ax.id] };
      }),
    [modes, boxes, steps, geometryLocked],
  );
  const plan = useMemo(
    () => deriveAxisPlan(rows.map(({ id, mode, lo, hi, steps: s, locked }) => ({ id, mode, lo, hi, steps: s, locked })), live),
    [rows, live],
  );
  const overCap = plan.kind === "grid" && plan.gridCount > MAX_SOLVE_POINTS;
  const rangeLabels = rows.filter((r) => r.mode !== "hold").map((r) => r.meta.label);

  // --- running campaign derivations (props-driven; the surface polls) --------
  const state = campaign?.state ?? null;
  const status = state?.status ?? null;
  const active = status === "running" || status === "paused";
  const runnerDead = status === "running" && campaign?.runner_alive === false;
  const completed = state?.completed ?? 0;
  const campaignBudget = campaign?.config?.budget ?? budget;
  const dense = useMemo(
    () => (campaign ? denseGridEquivalent(campaign.config?.axes ?? []) : null),
    [campaign],
  );
  const savings = warmStartSavings(state?.warm_started ?? 0);
  const statusColor =
    status === "running" ? palette.primary
    : status === "paused" ? palette.amber
    : status === "completed" ? palette.classOk
    : status === "failed" ? palette.warn
    : palette.ink3;

  const chip = (on: boolean, disabled = false): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontFamily: FONT_STACK,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    border: `1px solid ${on ? palette.primary : palette.line}`,
    background: on ? palette.primarySoft : palette.surface,
    color: on ? palette.primary : palette.ink3,
  });
  const numInput: React.CSSProperties = {
    width: 52, fontFamily: MONO_STACK, fontSize: 10, background: palette.surface2,
    border: `1px solid ${palette.line}`, color: palette.ink, borderRadius: 4, padding: "1px 4px",
  };
  const mono10: React.CSSProperties = { fontSize: 10, color: palette.ink4, fontFamily: MONO_STACK };

  const liveOf = (id: SweepAxisId): number => liveCanonicalValue(id, live);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="hlw-axis-table">
      {rows.map((r) => {
        const detail = r.locked
          ? `held · ${r.meta.fmt(liveOf(r.id))} · cloud-only (one staged mesh)`
          : r.mode === "hold"
            ? `hold · ${r.meta.fmt(liveOf(r.id))}`
            : r.mode === "sweep"
              ? `${r.meta.fmt(r.lo)} → ${r.meta.fmt(r.hi)} · ${Math.max(2, r.steps)} steps`
              : `box ${r.meta.fmt(r.lo)} … ${r.meta.fmt(r.hi)} · Tailor fills adaptively`;
        return (
          <div key={r.id} data-testid={`axis-row-${r.id}`} style={{ border: `1px solid ${!(r.lo < r.hi) && r.mode !== "hold" ? palette.warn : palette.line}`, borderRadius: 7, padding: "7px 9px", opacity: r.locked ? 0.7 : 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, color: palette.ink, fontFamily: FONT_STACK }}>{r.meta.label}</span>
              <span style={{ flex: 1 }} />
              {(["hold", "sweep", "range"] as const).map((m) => (
                <span
                  key={m}
                  role="button"
                  aria-pressed={r.mode === m}
                  title={
                    r.locked && m !== "hold"
                      ? "This axis needs a re-mesh per value — unavailable on a cloud-only host"
                      : m === "sweep"
                        ? "Explicit grid steps across the box (≤8-point batch)"
                        : m === "range"
                          ? "Box the axis — a Tailor campaign fills it adaptively"
                          : "Pin at the live value"
                  }
                  style={{ ...chip(r.mode === m, r.locked && m !== "hold"), marginLeft: 3 }}
                  onClick={() => (r.locked && m !== "hold" ? undefined : setModes((prev) => ({ ...prev, [r.id]: m })))}
                >
                  {m}
                </span>
              ))}
            </div>
            {/* Stable two-line layout (review fix: the caption used to share a
                flex row with the inputs and wrapped into them at the default
                inspector width): caption line, then a right-aligned inputs row. */}
            <div
              style={{ ...mono10, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={detail}
            >
              {detail}
            </div>
            {r.mode !== "hold" && !r.locked ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                <label style={{ ...mono10, color: palette.ink3, display: "inline-flex", alignItems: "center", gap: 3 }}>
                  min
                  <input
                    type="number"
                    value={r.lo}
                    step={r.meta.id === "mach" || r.meta.id === "disk_loading" ? 0.05 : 1}
                    onChange={(e) => setBoxes((b) => ({ ...b, [r.id]: [Number(e.target.value), b[r.id][1]] }))}
                    style={numInput}
                  />
                </label>
                <label style={{ ...mono10, color: palette.ink3, display: "inline-flex", alignItems: "center", gap: 3 }}>
                  max
                  <input
                    type="number"
                    value={r.hi}
                    step={r.meta.id === "mach" || r.meta.id === "disk_loading" ? 0.05 : 1}
                    onChange={(e) => setBoxes((b) => ({ ...b, [r.id]: [b[r.id][0], Number(e.target.value)] }))}
                    style={numInput}
                  />
                </label>
                {r.mode === "sweep" ? (
                  <label style={{ ...mono10, color: palette.ink3, display: "inline-flex", alignItems: "center", gap: 3 }}>
                    steps
                    <input
                      type="number"
                      min={2}
                      max={MAX_SOLVE_POINTS}
                      value={Math.max(2, r.steps)}
                      onChange={(e) => setSteps((s) => ({ ...s, [r.id]: Math.max(2, Math.min(MAX_SOLVE_POINTS, Number(e.target.value) || 2)) }))}
                      style={{ ...numInput, width: 42 }}
                    />
                  </label>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}

      {/* ── the ONE CTA — grid batch or Tailor campaign, by the rows above ── */}
      {plan.kind === "idle" ? (
        <span style={{ ...mono10, fontSize: 11 }}>
          every axis held — the spine's ▶ Solve runs the single live point; sweep or range an axis to build more.
        </span>
      ) : null}

      {plan.kind === "grid" ? (
        <>
          <span style={{ fontSize: 11, fontFamily: MONO_STACK, color: overCap ? palette.warn : palette.ink3 }}>
            {plan.gridCount} point{plan.gridCount === 1 ? "" : "s"}
            {overCap ? ` · over the ${MAX_SOLVE_POINTS}-point cap — trim steps/axes, or range an axis for a campaign` : ""}
          </span>
          <button
            type="button"
            data-testid="axis-cta-grid"
            disabled={busy || overCap || plan.gridCount === 0 || plan.invalid.length > 0}
            onClick={() => onSubmitGrid(plan.points)}
            style={{
              width: "100%", padding: 7, fontSize: 11, fontFamily: FONT_STACK, fontWeight: 600,
              border: `1px solid ${palette.primary}`,
              background: overCap || busy ? palette.surface2 : palette.primary,
              color: overCap || busy ? palette.ink3 : palette.onPrimary,
              borderRadius: 7, cursor: busy || overCap ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "submitting…" : `Submit ${plan.gridCount} → Tailor`}
          </button>
        </>
      ) : null}

      {plan.kind === "campaign" ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <label style={{ ...mono10, color: palette.ink3, display: "inline-flex", alignItems: "center", gap: 4 }}>
              budget
              <input
                type="number"
                min={CAMPAIGN_BUDGET_MIN}
                max={CAMPAIGN_BUDGET_MAX}
                value={budget}
                onChange={(e) => setBudget(Math.max(CAMPAIGN_BUDGET_MIN, Math.min(CAMPAIGN_BUDGET_MAX, Number(e.target.value) || BUDGET_DEFAULT)))}
                style={numInput}
              />
            </label>
            <label style={{ ...mono10, color: palette.ink3, display: "inline-flex", alignItems: "center", gap: 4 }}>
              batch
              <input
                type="number"
                min={BATCH_MIN}
                max={BATCH_MAX}
                value={batchSize}
                onChange={(e) => setBatchSize(Math.max(BATCH_MIN, Math.min(BATCH_MAX, Number(e.target.value) || BATCH_DEFAULT)))}
                style={{ ...numInput, width: 40 }}
              />
            </label>
            <span style={{ ...mono10, color: palette.ink3, display: "inline-flex", alignItems: "center", gap: 4 }}>
              stop at
              {COVERAGE_CHOICES.map((c) => (
                <span key={c} style={chip(targetCoverage === c)} onClick={() => setTargetCoverage(targetCoverage === c ? null : c)}>
                  {Math.round(c * 100)}%
                </span>
              ))}
            </span>
          </div>
          <button
            type="button"
            data-testid="axis-cta-campaign"
            disabled={busy || plan.invalid.length > 0 || active}
            title={active ? "A campaign is already running — control it below" : undefined}
            onClick={() =>
              onStartCampaign({
                name: `Tailor fill · ${rangeLabels.join(" × ")}`,
                axes: plan.axes,
                budget,
                batchSize,
                targetCoverage,
              })
            }
            style={{
              width: "100%", padding: 7, fontSize: 11, fontFamily: FONT_STACK, fontWeight: 600,
              border: `1px solid ${palette.primary}`,
              background: busy || active ? palette.surface2 : palette.primary,
              color: busy || active ? palette.ink3 : palette.onPrimary,
              borderRadius: 7, cursor: busy || active ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "starting…" : `Fill ${budget} points → Tailor campaign`}
          </button>
        </>
      ) : null}

      {/* ── attached campaign status + controls (props-driven) ── */}
      {campaign ? (
        <div style={{ border: `1px solid ${palette.line}`, borderRadius: 7, padding: "7px 9px", display: "flex", flexDirection: "column", gap: 5 }} data-testid="axis-campaign-status">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ ...mono10, fontSize: 11, color: statusColor, fontWeight: 600 }}>
              ● {runnerDead ? "stalled (runner gone)" : status ?? "?"}
            </span>
            <span style={{ ...mono10, fontSize: 11, color: palette.ink3 }}>
              {completed}/{campaignBudget} pts · coverage {coveragePercent(state?.coverage)}%
            </span>
            <span style={{ flex: 1 }} />
            {status === "running" && !runnerDead ? (
              <span style={chip(false)} onClick={() => onControlCampaign("pause")}>pause</span>
            ) : null}
            {status === "paused" || runnerDead ? (
              <span style={chip(false)} onClick={() => onControlCampaign("resume")}>resume</span>
            ) : null}
            {active ? (
              <span style={chip(false)} onClick={() => onControlCampaign("stop")}>stop</span>
            ) : null}
          </div>
          {/* Counters (review fix: observability lost with FillPanel): what the
              budget actually bought — dispatched / cache freebies / warm / failed. */}
          <span style={mono10} data-testid="axis-campaign-counters">
            dispatched {state?.dispatched ?? 0} · cache·free {state?.cache_hits ?? 0} · warm-started {state?.warm_started ?? 0} ·{" "}
            <span style={{ color: (state?.failed ?? 0) > 0 ? palette.warn : undefined }}>failed {state?.failed ?? 0}</span>
          </span>
          {/* Runner note — the honest failure/stop reason (review fix). */}
          {state?.note ? (
            <span
              style={{ ...mono10, fontSize: 11, color: status === "failed" || status === "stopped" ? palette.warn : palette.ink3 }}
              data-testid="axis-campaign-note"
            >
              {state.note}
            </span>
          ) : null}
          {(state?.warm_started ?? 0) > 0 ? (
            <span style={mono10}>{savings.label}</span>
          ) : null}
          {dense && dense.rangeAxes > 0 ? (
            <span style={mono10}>
              {completed} adaptive vs {dense.points} dense-grid points at the same per-axis resolution ({dense.formula})
              {(state?.cache_hits ?? 0) > 0 ? ` · ${state!.cache_hits} free from cache` : ""}
            </span>
          ) : null}
          {/* The acquisition's verbatim rationale, latest-first — collapsed so
              the table stays calm (review fix: the reason feed lost its home). */}
          {(state?.iterations?.length ?? 0) > 0 ? (
            <details data-testid="axis-campaign-reasons">
              <summary style={{ ...mono10, cursor: "pointer" }}>why these points</summary>
              <div style={{ display: "flex", flexDirection: "column", gap: 3, paddingTop: 4, maxHeight: 140, overflow: "auto" }}>
                {[...(state?.iterations ?? [])].reverse().map((it) => (
                  <span key={it.index} style={{ ...mono10, lineHeight: 1.45 }}>
                    #{it.index + 1} · {(it.reasons_top ?? []).join(" · ") || it.engine || "—"}
                  </span>
                ))}
              </div>
            </details>
          ) : null}
        </div>
      ) : null}

      {note ? <span style={{ ...mono10, fontSize: 11, color: palette.warn }}>{note}</span> : null}
    </div>
  );
}
